import { useState } from "react";
import { useWallet } from "../wallet/WalletContext";
import { adminAccounts } from "../config";

// Deliberately the same localStorage key the community chat uses, so
// someone who already picked a name in the chat widget doesn't get asked
// again on the forum (and vice versa).
const GUEST_NAME_KEY = "waxcreator-chat-guest-name";

export interface ForumIdentity {
  /** WAX account if connected, else the saved guest name, else "" — an
   *  empty string is fine: the server files those posts as "anon". */
  displayName: string;
  isWallet: boolean;
  isAdmin: boolean;
  account: string | undefined;
  guestName: string;
  setGuestName: (name: string) => void;
}

/**
 * Who the visitor is posting as, in the loosest sense this site needs:
 * a connected WAX account, a remembered guest name, or nobody at all.
 * There is no registration and no verification — see forum-post.ts for
 * why that's the deliberate choice here and what it costs.
 */
export function useForumIdentity(): ForumIdentity {
  const { account } = useWallet();
  const [guestName, setGuestNameState] = useState(() => {
    try {
      return localStorage.getItem(GUEST_NAME_KEY) || "";
    } catch {
      return "";
    }
  });

  function setGuestName(name: string) {
    const trimmed = name.trim().slice(0, 24);
    setGuestNameState(trimmed);
    try {
      if (trimmed) localStorage.setItem(GUEST_NAME_KEY, trimmed);
      else localStorage.removeItem(GUEST_NAME_KEY);
    } catch {
      /* private browsing / storage disabled — the name just won't stick */
    }
  }

  return {
    displayName: account || guestName,
    isWallet: !!account,
    isAdmin: !!account && adminAccounts.includes(account),
    account,
    guestName,
    setGuestName,
  };
}
